/**
 * The app shell, in two shapes (P0-TR4, NFR-1).
 *
 * Below `md` this stays the phone app the plan asks for: bottom navigation,
 * because the bottom of the screen is what a thumb reaches and a partner
 * standing in a shed is the primary user (NFR-1, S1).
 *
 * At `md` and above it becomes a conventional admin panel — a persistent
 * sidebar with every destination, and a top bar. Both render exactly the same
 * routes: a figure a partner sees on a phone is the figure an admin sees on a
 * laptop, which is the whole basis of §1.4's transparency.
 *
 * Two navigations rather than one that reflows, because they are genuinely
 * different things: four thumb-sized destinations against eighteen grouped
 * links. One list trying to be both would be a poor sidebar and a worse tab
 * bar. Only one is in the accessibility tree at a time — the other is
 * `hidden`, not merely moved off-screen, so a screen reader is not read both.
 */

import { NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '../../features/auth/AuthContext.js';
import { LOCALE_LABELS, type Locale } from '../../i18n/index.js';
import { useTranslation } from '../../i18n/useTranslation.js';

/**
 * Inline SVG path data rather than an icon package: a handful of glyphs is not
 * worth the kilobytes on a 3G connection (NFR-3). Path data rather than ready
 * elements so the same glyph renders at the size each navigation wants — the
 * tab bar's thumb-sized 24px, the sidebar's 20px beside a line of text.
 */
const ICONS = {
  home: 'M3 10.5 12 3l9 7.5M5 9.5V21h14V9.5',
  money: 'M12 3v18M8 7h6a3 3 0 0 1 0 6H8m0 0h8',
  animals: 'M4 10a3 3 0 0 1 6 0v6H4zM14 10a3 3 0 0 1 6 0v6h-6zM10 16h4',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  /** Clipboard — the day's written-down record. */
  dailyLog: 'M9 4H6v16h12V4h-3M9 3h6v3H9zM9 12h6M9 16h4',
  /** Milk glass. */
  milk: 'M7 4h10l-1.5 16h-7zM7.6 10h8.8',
  /** Stacked carton. */
  inventory: 'M12 3 3 7.5V17l9 4.5 9-4.5V7.5zM3 7.5 12 12l9-4.5M12 12v9.5',
  /** Bell. */
  reminders: 'M18 16v-5a6 6 0 1 0-12 0v5l-2 2h16zM10 21h4',
  /** Delivery van for the milk round. */
  milkRound:
    'M3 7h11v9H3zM14 10h4l3 3v3h-7zM7.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M17.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3',
  /** Bar chart. */
  reports: 'M4 20h16M8 20v-6M12 20V8M16 20v-9',
  /** Money coming in. */
  receivables: 'M12 3v10m0 0 4-4m-4 4-4-4M4 16v4h16v-4',
  /** Two figures swapping — the two sides being matched. */
  reconcile: 'M4 8h13l-3-3M20 16H7l3 3',
  /** Ledger. */
  books: 'M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2zM6 17h13',
  /** Two people. */
  partners:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1M17 4.7a3 3 0 0 1 0 5.9M18.5 14.2A5 5 0 0 1 21 18.6V20',
  /** Shopfront. */
  vendors: 'M4 10h16v10H4zM3 10l2-5h14l2 5M9 20v-6h6v6',
  /** Building — the farm's fixed property. */
  assets: 'M3 21h18M6 21V10l6-4 6 4v11M10 21v-5h4v5',
  /** Calendar. */
  meetings: 'M4 6h16v14H4zM8 3v4M16 3v4M4 10h16',
  /** Checklist. */
  tasks: 'M10 7h10M10 12h10M10 17h10M4 7l1.5 1.5L8 5.5M4 16.5 5.5 18 8 15',
  /** Magnifier over the record. */
  audit: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14M16 16l4.5 4.5',
  /** Crescent — the season is fixed by the Hijri calendar. */
  qurbani: 'M20 14.5A8 8 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5',
  /** Camera. */
  cameras: 'M4 7h11v10H4zM15 10.5 20 8v8l-5-2.5',
  /** A hand offering a bottle — the people the farm sells milk to. */
  customers: 'M7 8h6l1 12H6zM9 8V5a2 2 0 0 1 4 0v3M17 11a3 3 0 1 0 0 6 3 3 0 0 0 0-6M17 17v3',
  /** A person with a plus — the roster, and adding to it. */
  users: 'M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M3 20v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1M18 4.5v5M15.5 7h5',
} as const;

function Icon({ path, className }: { path: string; className: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className={className}
      aria-hidden="true"
    >
      <path d={path} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface NavItem {
  to: string;
  labelKey: string;
  icon: string;
}

/** The phone's four destinations, from the source plan. */
const NAV_ITEMS: NavItem[] = [
  { to: '/', labelKey: 'nav.home', icon: ICONS.home },
  { to: '/money', labelKey: 'nav.money', icon: ICONS.money },
  { to: '/animals', labelKey: 'nav.animals', icon: ICONS.animals },
  { to: '/more', labelKey: 'nav.more', icon: ICONS.more },
];

/**
 * The desktop sidebar, grouped as the More page groups things.
 *
 * Every destination, not a useful subset: an admin panel that quietly omits a
 * screen is one where somebody eventually asks why two numbers disagree.
 *
 * Each link carries a glyph. Eighteen labels in one column read as a wall of
 * Bangla text; a shape in front of each one gives the eye something to aim at,
 * and a partner who uses two screens learns them by position and picture
 * rather than by reading every time. The glyph is decorative — the label is
 * still the accessible name.
 */
const SIDEBAR_GROUPS: { titleKey: string; links: NavItem[] }[] = [
  {
    titleKey: 'nav.overview',
    links: [
      { to: '/', labelKey: 'nav.home', icon: ICONS.home },
      { to: '/money', labelKey: 'nav.money', icon: ICONS.money },
      { to: '/animals', labelKey: 'nav.animals', icon: ICONS.animals },
    ],
  },
  {
    titleKey: 'more.daily',
    links: [
      { to: '/operations/daily-log', labelKey: 'daily.title', icon: ICONS.dailyLog },
      { to: '/operations/milk', labelKey: 'milk.title', icon: ICONS.milk },
      { to: '/operations/inventory', labelKey: 'inventory.title', icon: ICONS.inventory },
      { to: '/operations/milk-round', labelKey: 'milk.delivery.title', icon: ICONS.milkRound },
    ],
  },
  {
    titleKey: 'more.money',
    links: [
      { to: '/reports', labelKey: 'reports.title', icon: ICONS.reports },
      { to: '/receivables', labelKey: 'receivables.title', icon: ICONS.receivables },
      { to: '/reconcile', labelKey: 'reconcile.title', icon: ICONS.reconcile },
      { to: '/customers', labelKey: 'customers.title', icon: ICONS.customers },
    ],
  },
  {
    titleKey: 'more.records',
    links: [
      { to: '/books', labelKey: 'finance.books.title', icon: ICONS.books },
      { to: '/partners', labelKey: 'finance.partners.title', icon: ICONS.partners },
      { to: '/vendors', labelKey: 'finance.vendors.title', icon: ICONS.vendors },
      { to: '/assets', labelKey: 'finance.assets.title', icon: ICONS.assets },
      { to: '/setup-costs', labelKey: 'setup.title', icon: ICONS.assets },
    ],
  },
  {
    titleKey: 'more.governance',
    links: [
      { to: '/meetings', labelKey: 'meetings.title', icon: ICONS.meetings },
      { to: '/tasks', labelKey: 'tasks.title', icon: ICONS.tasks },
      { to: '/audit', labelKey: 'audit.title', icon: ICONS.audit },
      { to: '/qurbani', labelKey: 'qurbani.title', icon: ICONS.qurbani },
      { to: '/cameras', labelKey: 'cctv.title', icon: ICONS.cameras },
      // Listed for everyone, not just admins: who has access to the books is
      // itself something §1.4 says every partner may see.
      { to: '/users', labelKey: 'users.title', icon: ICONS.users },
    ],
  },
];

function Sidebar(): JSX.Element {
  const { t } = useTranslation();

  return (
    <aside
      aria-label={t('nav.sidebar')}
      className="fixed inset-y-0 left-0 hidden w-60 overflow-y-auto border-r border-gray-200 bg-white md:block"
    >
      <div className="px-5 py-4">
        <p className="text-base font-semibold text-green-800">{t('app.name')}</p>
      </div>

      <nav className="px-3 pb-6">
        {SIDEBAR_GROUPS.map((group) => (
          <div key={group.titleKey} className="mb-4">
            <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-400">{t(group.titleKey)}</p>
            <ul>
              {group.links.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end={link.to === '/'}
                    className={({ isActive }) =>
                      `flex min-h-touch items-center gap-2.5 rounded-lg px-2 py-2 text-sm ${
                        isActive ? 'bg-green-50 font-medium text-green-800' : 'text-gray-700 hover:bg-gray-50'
                      }`
                    }
                  >
                    {/* shrink-0 so a long Bangla label wraps around the glyph
                        rather than squashing it. */}
                    <Icon path={link.icon} className="h-5 w-5 shrink-0" />
                    <span>{t(link.labelKey)}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}

function TopBar(): JSX.Element {
  const { t, locale } = useTranslation();
  const { user, logout } = useAuth();

  return (
    <header className="hidden border-b border-gray-200 bg-white px-6 py-3 md:flex md:items-center md:justify-between">
      <p className="text-sm text-gray-600">{user?.fullName ?? ''}</p>

      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-400">{LOCALE_LABELS[locale as Locale]}</span>
        <button
          type="button"
          onClick={() => void logout()}
          className="min-h-touch rounded-lg px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          {t('auth.logout')}
        </button>
      </div>
    </header>
  );
}

export function AppShell(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      {/* The sidebar is fixed, so content is inset by its width at md+. */}
      <div className="md:pl-60">
        <TopBar />

        {/* pb-20 keeps content clear of the fixed bottom bar on a phone. The
            bar is gone at md+, so the padding goes with it. */}
        <main className="pb-20 md:pb-8">
          <Outlet />
        </main>
      </div>

      <nav
        aria-label={t('nav.primary')}
        className="fixed inset-x-0 bottom-0 z-10 border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        <ul className="mx-auto flex max-w-md">
          {NAV_ITEMS.map((item) => (
            <li key={item.to} className="flex-1">
              <NavLink
                to={item.to}
                // `end` so Home is only active on "/" rather than every route.
                end={item.to === '/'}
                className={({ isActive }) =>
                  `flex min-h-touch flex-col items-center gap-0.5 px-1 py-2 text-xs ${
                    isActive ? 'font-medium text-green-700' : 'text-gray-500'
                  }`
                }
              >
                <Icon path={item.icon} className="h-6 w-6" />
                <span>{t(item.labelKey)}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
